// ==============================================
// src/contexts/AuthContext.js  — FULL FILE (drop-in)
// Fixes:
// 1) Dev login now uses a FIXED email/password (no more anonymous accounts)
// 2) Dev badge/panels only show if role === "Developer" from Firestore
// 3) Clean role loading to prevent flicker
// ==============================================

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// From .env.local (development only)
// REACT_APP_DEV_EMAIL=dev@example.com
// REACT_APP_DEV_PASSWORD=supersecret
const DEV_EMAIL = process.env.REACT_APP_DEV_EMAIL;
const DEV_PASSWORD = process.env.REACT_APP_DEV_PASSWORD;
const DEV_ENABLED = process.env.NODE_ENV === 'development' && !!DEV_EMAIL && !!DEV_PASSWORD;

async function ensureUserDoc(uid, base = {}) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { ...base, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  } else {
    // Make sure updatedAt exists
    await setDoc(ref, { updatedAt: serverTimestamp(), ...base }, { merge: true });
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // 'Developer' | 'User' | null while loading
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Subscribe to auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setRole(null);
        setLoadingAuth(false);
        return;
      }

      try {
        const ref = doc(db, 'users', u.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setRole(data.role || 'User');
        } else {
          // Create a default user doc if missing
          await ensureUserDoc(u.uid, { email: u.email || '', name: u.displayName || '', role: 'User' });
          setRole('User');
        }
      } catch (e) {
        console.warn('Failed to read user role:', e);
        setRole('User');
      } finally {
        setLoadingAuth(false);
      }
    });
    return () => unsub();
  }, []);

  const isDev = useMemo(() => role === 'Developer', [role]);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const devSignIn = async () => {
    if (!DEV_ENABLED) throw new Error('Dev login disabled. Set REACT_APP_DEV_EMAIL/PASSWORD and run in development.');
    const cred = await signInWithEmailAndPassword(auth, DEV_EMAIL, DEV_PASSWORD);
    // Ensure Firestore user doc marks this account as Developer
    await ensureUserDoc(cred.user.uid, { role: 'Developer', email: DEV_EMAIL, name: 'Developer' });
    // Make sure the displayName says Developer (nice for the header)
    try { await updateProfile(cred.user, { displayName: 'Developer' }); } catch (_) {}
    setRole('Developer');
    return cred.user;
  };

  const logout = () => signOut(auth);

  const value = { user, role, isDev, loadingAuth, login, logout, devSignIn, DEV_ENABLED };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ==============================================
// HOW TO WIRE IT
// ==============================================
// 1) Wrap your app in <AuthProvider> (index.js or App.js root)
//    import { AuthProvider } from './contexts/AuthContext';
//
//    export default function AppRoot() {
//      return (
//        <AuthProvider>
//          <App />
//        </AuthProvider>
//      );
//    }
//
// 2) Add a Dev Login button (only shows in development when .env vars are set)
//    // In LoginPage.js
//    import { useAuth } from '../contexts/AuthContext';
//    const { devSignIn, DEV_ENABLED } = useAuth();
//
//    {DEV_ENABLED && (
//      <button
//        type="button"
//        onClick={async () => { try { await devSignIn(); navigate('/'); } catch(e){ alert(e.message); } }}
//        className="w-full bg-gray-800 text-white p-3 rounded hover:bg-gray-900"
//        style={{ marginBottom: '0.5rem' }}
//      >
//        Dev Login (no password)
//      </button>
//    )}
//
// 3) Show a DEV badge (Header.js)
//    import { useAuth } from '../contexts/AuthContext';
//    const { isDev, loadingAuth } = useAuth();
//    {!loadingAuth && isDev && (
//       <span className="ml-2 text-xs px-2 py-1 rounded bg-purple-600 text-white">DEV</span>
//    )}
//
// 4) Gate Developer-only panels (e.g., HomePage inbox)
//    const { isDev, loadingAuth } = useAuth();
//    {!loadingAuth && isDev && (
//      <DeveloperInboxPanel />
//    )}
//
// 5) .env.local (never commit)
//    REACT_APP_DEV_EMAIL=dev@example.com
//    REACT_APP_DEV_PASSWORD=supersecret
//
//    Restart the dev server after creating/updating .env.local.
//
// Notes:
// - We REMOVED anonymous dev login to avoid random UIDs. Use the fixed dev account instead.
// - Firestore rules for dev inbox should check role === 'Developer' on users/{uid}.
// - If your dev account doesn’t exist yet, create it once in Firebase Auth (email/password),
//   then hit the Dev Login. The context will tag it with role: 'Developer' automatically.



